/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 *
 * Home tab -- the command center: a health banner, one tile per device
 * category, the open problems, quick access and the live widgets. Panel
 * styling lives in styles/grid-home.css (`.gh`) and styles/command-center.css.
 */

import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ClockIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ShareIcon,
  WifiIcon,
  ServerIcon,
  ServerStackIcon,
  CubeIcon,
  GlobeAltIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import { DeleteConfirmationDialog } from "@/components/common/DeleteConfirmationDialog";
import { showToast } from "@/components/common/Toast";
import { ServiceForm } from "./ServiceForm";
import { QuickAccessWidget } from "./QuickAccessWidget";
import { IncidentsModal } from "./IncidentsModal";
import { NetworkMapModal } from "./NetworkMapModal";
import { getOverview, getIncidents, getAnomalies, muteProblem, unmuteProblem, type Overview, type OverviewCategory } from "@/api/overview";
import { MuteButton } from "./MuteButton";
import { AnimatePresence, motion } from "motion/react";
import { CheckBadge } from "@/components/common/CheckBadge";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { ResourcesWidget, NetworkWidget } from "./widgets/ServerWidgets";
import {
  ProcessesWidget,
  DiskHealthWidget,
  VpnWidget,
  EventLogWidget,
  ServiceUptimeWidget,
} from "./widgets/FleetWidgets";
import { SpeedtestWidget } from "./widgets/SpeedtestWidget";
import { LiveRequestsWidget } from "./widgets/LiveRequestsWidget";
import { AdGuardWidget } from "./widgets/AdGuardWidget";
import { getAdGuardStats } from "@/api/adguard";
import { SelfWidget } from "./widgets/SelfWidget";
import { FleetTopWidget } from "./widgets/FleetTopWidget";
import { PageHeader } from "@/components/common/PageHeader";
import {
  getServices,
  createService,
  updateService,
  deleteService,
  Service,
  CreateServiceRequest,
  UpdateServiceRequest,
} from "@/api/services";
import { getRouters } from "@/api/routers";
import { tailscaleAPI } from "@/api/tailscale";
import { getRecentRequests } from "@/api/requests";
import { getMonitorAgents } from "@/api/monitor";
import { getDockerHosts } from "@/api/docker";
import { getSNMPDevices } from "@/api/snmp";
import { SERVICES_REFRESH_INTERVALS } from "@/constants/servicesRefreshIntervals";
import { useLanguage } from "@/utils/language";
import { useTabNavigationContext } from "@/context/tabNavigation";

type TileState = "ok" | "warn" | "crit" | "idle";

function tileState(cat?: OverviewCategory): TileState {
  if (!cat || cat.total === 0) return "idle";
  if (cat.up === 0) return "crit";
  if (cat.up < cat.total) return "warn";
  return "ok";
}

const TILES: {
  key: keyof Overview["categories"];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tab?: string;
}[] = [
  { key: "routers", label: "Routers", icon: WifiIcon, tab: "routers" },
  { key: "agents", label: "Servers", icon: ServerIcon, tab: "monitor" },
  { key: "switches", label: "Switches", icon: ServerStackIcon, tab: "switches" },
  { key: "docker", label: "Docker", icon: CubeIcon, tab: "docker" },
  { key: "services", label: "Services", icon: GlobeAltIcon },
  { key: "packetloss", label: "Packet loss", icon: SignalIcon, tab: "network" },
];

export const HomeTab: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { onTabChange } = useTabNavigationContext();
  const [mapOpen, setMapOpen] = useState(false);
  const [incidentsOpen, setIncidentsOpen] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  const { data: services = [], isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: getServices,
    refetchInterval: SERVICES_REFRESH_INTERVALS.LIST,
  });
  const { data: routers = [] } = useQuery({
    queryKey: ["routers"],
    queryFn: getRouters,
    refetchInterval: 60_000,
  });
  const { data: agents = [] } = useQuery({
    queryKey: ["monitor-agents"],
    queryFn: getMonitorAgents,
    refetchInterval: 30_000,
  });
  const { data: dockerHosts = [] } = useQuery({
    queryKey: ["docker-hosts"],
    queryFn: getDockerHosts,
    refetchInterval: 30_000,
  });
  const { data: switches = [] } = useQuery({
    queryKey: ["snmp-devices"],
    queryFn: getSNMPDevices,
    refetchInterval: 30_000,
  });
  const { data: overview } = useQuery({
    queryKey: ["overview"],
    queryFn: getOverview,
    refetchInterval: 30_000,
  });
  const { data: incidents = [] } = useQuery({
    queryKey: ["incidents"],
    queryFn: getIncidents,
    refetchInterval: 30_000,
  });
  const { data: anomalies = [] } = useQuery({
    queryKey: ["anomalies"],
    queryFn: getAnomalies,
    refetchInterval: 30_000,
  });

  // Default to a server that is actually reporting: the first enabled agent that
  // the overview does not list as a problem (offline agents have no data to show).
  const downAgents = useMemo(
    () => new Set((overview?.problems ?? []).filter((p) => p.type === "agent").map((p) => p.name)),
    [overview],
  );
  const primaryAgent = useMemo(
    () => agents.find((a) => a.enabled && !downAgents.has(a.name)) ?? agents.find((a) => a.enabled) ?? agents[0],
    [agents, downAgents],
  );

  // Which agent the Home Resources/Network widgets show. Persisted so the
  // choice survives a reload; falls back to the primary agent.
  const [homeAgentId, setHomeAgentId] = useState<number | null>(() => {
    const v = Number(localStorage.getItem("nyrvex-home-agent"));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const setHomeAgent = useCallback((id: number) => {
    setHomeAgentId(id);
    try {
      localStorage.setItem("nyrvex-home-agent", String(id));
    } catch {
      /* ignore */
    }
  }, []);
  const homeAgent = useMemo(
    () => agents.find((a) => a.id === homeAgentId) ?? primaryAgent,
    [agents, homeAgentId, primaryAgent],
  );

  const createMutation = useMutation({
    mutationFn: createService,
    onSuccess: (newService) => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setIsFormOpen(false);
      showToast("Service added", "success", { description: `${newService.name} added` });
    },
    onError: (error: Error) =>
      showToast("Failed to add service", "error", { description: error.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateServiceRequest }) => updateService(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setIsFormOpen(false);
      setEditingService(null);
      showToast("Service updated", "success");
    },
    onError: (error: Error) =>
      showToast("Failed to update service", "error", { description: error.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      showToast("Service deleted", "success", {
        description: `${serviceToDelete?.name || "Service"} removed`,
      });
    },
    onError: (error: Error) =>
      showToast("Failed to delete service", "error", { description: error.message }),
  });

  const handleEdit = useCallback((service: Service) => {
    setEditingService(service);
    setIsFormOpen(true);
  }, []);
  const handleDelete = useCallback((service: Service) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  }, []);
  const confirmDelete = () => {
    if (serviceToDelete) {
      setDeleteDialogOpen(false);
      deleteMutation.mutate(serviceToDelete.id);
      setServiceToDelete(null);
    }
  };
  const handleFormSubmit = (data: CreateServiceRequest) => {
    if (editingService) {
      updateMutation.mutate({ id: editingService.id, data: { ...data, id: editingService.id } });
    } else {
      createMutation.mutate(data);
    }
  };

  // VPN and live-requests tiles only earn their space when they're configured.
  const { data: vpn } = useQuery({
    queryKey: ["tailscale-status"],
    queryFn: () => tailscaleAPI.getDiscoveryStatus(),
    refetchInterval: 30_000,
    retry: false,
  });
  const { data: reqProbe } = useQuery({
    queryKey: ["live-requests-enabled"],
    queryFn: () => getRecentRequests(0, 1),
    refetchInterval: 60_000,
    retry: false,
  });
  const { data: dns } = useQuery({ queryKey: ["adguard"], queryFn: getAdGuardStats, refetchInterval: 60_000, retry: false });
  const showDns = !!dns?.configured;
  const showVpn = !!vpn?.enabled;
  const showRequests = !!reqProbe?.enabled;
  const bottomCount = 2 + Number(showVpn) + Number(showRequests) + Number(showDns);

  const muted = overview?.muted ?? [];
  const [showMuted, setShowMuted] = useState(false);
  const refreshOverview = () => queryClient.invalidateQueries({ queryKey: ["overview"] });
  const problems = overview?.problems ?? [];
  const hasCrit = problems.some((p) => p.severity === "crit");
  const cats = Object.values(overview?.categories ?? {});
  const totalUp = cats.reduce((n, c) => n + c.up, 0);
  const totalAll = cats.reduce((n, c) => n + c.total, 0);

  return (
    <div className="gh cc">
      <PageHeader
        title="Home"
        subtitle="Status of your whole infrastructure"
      />

      <section className={`cc-health cc-health--${hasCrit ? "crit" : problems.length ? "warn" : "ok"}`}>
        <div className="cc-hero">
          <span className="cc-hero-ico">
            {problems.length === 0 ? (
              <CheckBadge size={34} draw />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6" />
            )}
          </span>
          <div className="cc-hero-txt">
            <h2>
              {problems.length === 0
                ? "All systems operational"
                : `${problems.length} ${problems.length === 1 ? "problem needs" : "problems need"} attention`}
            </h2>
            <p>
              {`${totalUp}/${totalAll} checks healthy${
                anomalies.length > 0 ? ` · ${anomalies.length} ${anomalies.length === 1 ? "anomaly" : "anomalies"}` : ""
              }`}
            </p>
          </div>
          <div className="cc-hero-act">
            <button type="button" className="gh-btn" onClick={() => setMapOpen(true)}>
              <ShareIcon className="h-3.5 w-3.5 rotate-90" />
              Network map
            </button>
            <button
              type="button"
              className="gh-btn"
              disabled={incidents.length === 0}
              onClick={() => setIncidentsOpen(true)}
            >
              <ClockIcon className="h-3.5 w-3.5" />
              Incident history
              {incidents.length > 0 && <span className="gh-inc-count">{incidents.length}</span>}
            </button>
          </div>
        </div>

        <div className="cc-tiles">
          {TILES.map(({ key, label, icon: Icon, tab }) => {
            const cat = overview?.categories[key];
            const state = tileState(cat);
            const pct = cat && cat.total > 0 ? Math.round((cat.up / cat.total) * 100) : 0;
            const down = cat ? cat.total - cat.up : 0;
            const Tag = tab ? "button" : "div";
            return (
              <Tag
                key={key}
                type={tab ? "button" : undefined}
                className={`cc-tile cc-tile--${state}${tab ? " cc-tile--link" : ""}`}
                onClick={tab ? () => onTabChange(tab) : undefined}
              >
                <span className="cc-tile-top">
                  <Icon className="h-4 w-4" />
                  {label}
                </span>
                <span className="cc-tile-num">
                  {cat ? <AnimatedNumber value={cat.up} /> : "–"}
                  <small>{`/${cat ? cat.total : "–"}`}</small>
                </span>
                <span className="cc-tile-bar">
                  <i style={{ width: `${pct}%` }} />
                </span>
                <span className="cc-tile-state">
                  {state === "ok" ? (<><CheckBadge size={13} />all up</>) : state === "crit" ? "✕ all down" : state === "warn" ? `! ${down} down` : "no devices"}
                </span>
              </Tag>
            );
          })}
        </div>
      </section>

      <div className="cc-row cc-row--top">
        <section className="gh-w cc-problems">
          <div className="gh-w-head">
            <span className="gh-label">Open problems</span>
            <span className="gh-sub">{problems.length + anomalies.length}</span>
          </div>
          {problems.length + anomalies.length === 0 ? (
            <div className="cc-empty">
              <CheckBadge size={34} />
              Nothing needs your attention
            </div>
          ) : (
            <ul className="cc-plist">
              <AnimatePresence initial={false}>
              {problems.map((p) => (
                <motion.li
                  key={`${p.type}-${p.name}`}
                  layout="position"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0.12 } }}
                  transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
                >
                  {p.severity === "crit" ? (
                    <XCircleIcon className="h-4 w-4 flex-none text-[var(--gh-crit)]" aria-label="critical" />
                  ) : (
                    <ExclamationTriangleIcon className="h-4 w-4 flex-none text-[var(--gh-warn)]" aria-label="warning" />
                  )}
                  <b>{p.name}</b>
                  <em>{p.type}</em>
                  <code>{p.reason}</code>
                  <MuteButton
                    onMute={(seconds) =>
                      muteProblem({ type: p.type, name: p.name, reason: p.reason, seconds }).then(refreshOverview)
                    }
                  />
                </motion.li>
              ))}
              </AnimatePresence>
              {anomalies.map((a, i) => (
                <li key={`${a.monitor}-${a.metric}-${i}`}>
                  <BoltIcon className="h-4 w-4 flex-none text-[var(--gh-warn)]" aria-label="anomaly" />
                  <b>{a.monitor}</b>
                  <em>anomaly</em>
                  <code>
                    {a.metric} {a.current}
                    {a.unit} vs {a.baseline}
                    {a.unit}
                  </code>
                </li>
              ))}
            </ul>
          )}
          {muted.length > 0 && (
            <div className="cc-muted">
              <button type="button" className="cc-muted-toggle" onClick={() => setShowMuted((v) => !v)}>
                {`Muted (${muted.length})`}
              </button>
              {showMuted && (
                <ul>
                  {muted.map((m) => (
                    <li key={`${m.type}-${m.name}`}>
                      <b>{m.name}</b>
                      <em>{m.until === 0 ? "forever" : `until ${new Date(m.until * 1000).toLocaleDateString()}`}</em>
                      <button
                        type="button"
                        onClick={() => unmuteProblem({ type: m.type, name: m.name }).then(refreshOverview)}
                      >
                        Unmute
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        <div className="cc-cell cc-cell--fit">
          <FleetTopWidget agents={agents} />
        </div>

        <div className="cc-side">
          <SpeedtestWidget agents={agents} />
          <div className="cc-qa">
            <QuickAccessWidget
              services={services}
              isLoading={isLoading}
              onAddService={() => {
                setEditingService(null);
                setIsFormOpen(true);
              }}
              onEditService={handleEdit}
              onDeleteService={handleDelete}
            />
          </div>
        </div>
      </div>

      <div className="cc-row cc-row--3">
        <div className="cc-cell cc-cell--fit">
          <ResourcesWidget agent={homeAgent} agents={agents} onAgentChange={setHomeAgent} />
        </div>
        <div className="cc-cell cc-cell--fit">
          <NetworkWidget agent={homeAgent} agents={agents} onAgentChange={setHomeAgent} />
        </div>
        <div className="cc-cell cc-cell--fit">
          <SelfWidget />
        </div>
      </div>

      <div className="cc-row cc-row--2">
        <div className="cc-cell cc-cell--fit">
          <EventLogWidget />
        </div>
        <div className="cc-cell cc-cell--fit">
          <ServiceUptimeWidget />
        </div>
      </div>

      <div className={`cc-row cc-row--bottom cc-row--${bottomCount}`}>
        <div className="cc-cell cc-cell--fit">
          <ProcessesWidget agent={homeAgent} agents={agents} onAgentChange={setHomeAgent} />
        </div>
        <div className="cc-cell cc-cell--fit">
          <DiskHealthWidget agent={homeAgent} agents={agents} onAgentChange={setHomeAgent} />
        </div>
        {showVpn && (
          <div className="cc-cell cc-cell--fit">
            <VpnWidget />
          </div>
        )}
        {showRequests && (
          <div className="cc-cell cc-cell--fit">
            <LiveRequestsWidget />
          </div>
        )}
        {showDns && dns && (
          <div className="cc-cell cc-cell--fit">
            <AdGuardWidget stats={dns} />
          </div>
        )}
      </div>

      <IncidentsModal open={incidentsOpen} onOpenChange={setIncidentsOpen} incidents={incidents} />
      <NetworkMapModal
        open={mapOpen}
        onOpenChange={setMapOpen}
        overview={overview}
        agents={agents}
        switches={switches}
        dockerHosts={dockerHosts}
        routers={routers}
        onTabChange={onTabChange}
      />

      <ServiceForm
        service={editingService}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setEditingService(null);
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        isOpen={isFormOpen}
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setServiceToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t("deleteService")}
        itemName={serviceToDelete?.name || ""}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
};
